<template>
    <div class="qr-scan-display">
        <!-- Loading State -->
        <div class="loading-container" v-if="loading">
            <div class="spinner-wrapper">
                <Spin size="large"></Spin>
            </div>
            <p class="loading-text">{{ $t("common.loading") }}</p>
        </div>

        <!-- Error State -->
        <div class="error-container" v-else-if="error">
            <div class="error-icon-wrapper">
                <Icon type="ios-alert" size="64" />
            </div>
            <h2 class="error-title">{{ $t("branch.invalidQRCode") }}</h2>
            <p class="error-message">{{ errorMessage }}</p>
            <Button type="primary" size="large" @click="$router.push('/')">
                <Icon type="ios-home" />
                {{ $t("branch.backToHome") }}
            </Button>
        </div>

        <!-- Main Content -->
        <div class="content-container" v-else-if="branchData">
            <!-- Header with Company Info -->
            <div class="company-header">
                <div class="company-content">
                    <div class="logo-wrapper">
                        <img
                            v-if="
                                branchData.company &&
                                    branchData.company.company_logo
                            "
                            :src="branchData.company.company_logo"
                            alt="Company Logo"
                            class="company-logo"
                        />
                        <div v-else class="no-logo">
                            <Icon type="ios-business" size="48" />
                        </div>
                    </div>
                    <div class="company-info">
                        <h1 class="company-name-en">
                            {{
                                branchData.company
                                    ? branchData.company.company_name_en
                                    : ""
                            }}
                        </h1>
                        <h2 class="company-name-kh">
                            {{
                                branchData.company
                                    ? branchData.company.company_name_kh
                                    : ""
                            }}
                        </h2>
                    </div>
                </div>
            </div>

            <!-- Certificate Section -->
            <div class="certificate-section">
                <div class="section-header">
                    <Icon type="ios-document-outline" size="28" />
                    <h3>វិញ្ញាបនបត្រ</h3>
                </div>

                <!-- Has photos -->
                <div
                    class="certificate-grid"
                    v-if="certificatePhotos.length > 0"
                >
                    <div
                        v-for="(photo, idx) in certificatePhotos"
                        :key="idx"
                        class="certificate-wrapper"
                        @click="openModal(idx)"
                    >
                        <img
                            :src="photo"
                            :alt="`Branch Certificate ${idx + 1}`"
                            class="certificate-image"
                        />
                        <div class="image-overlay">
                            <Icon type="ios-expand" size="28" />
                            <span>ចុចដើម្បីមើលទំហំពេញ</span>
                        </div>
                    </div>
                </div>

                <!-- No photos -->
                <div class="no-certificate" v-else>
                    <Icon type="ios-image-outline" size="80" />
                    <p>{{ $t("branch.noCertificate") }}</p>
                </div>
            </div>
        </div>

        <!-- Full Image Modal -->
        <Modal
            v-model="showImageModal"
            :footer-hide="true"
            width="90%"
            class-name="full-image-modal"
        >
            <div class="modal-content" v-if="activePhoto">
                <!-- Navigation arrows when multiple photos -->
                <button
                    v-if="certificatePhotos.length > 1"
                    class="modal-nav modal-nav--prev"
                    @click="prevPhoto"
                >
                    <Icon type="ios-arrow-back" size="28" />
                </button>

                <img
                    :src="activePhoto"
                    alt="Branch Certificate"
                    class="modal-image"
                />

                <button
                    v-if="certificatePhotos.length > 1"
                    class="modal-nav modal-nav--next"
                    @click="nextPhoto"
                >
                    <Icon type="ios-arrow-forward" size="28" />
                </button>

                <div class="modal-counter" v-if="certificatePhotos.length > 1">
                    {{ activeIndex + 1 }} / {{ certificatePhotos.length }}
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import axios from "axios";
import AppOptions from "@/config/AppOptions.vue";

export default {
    name: "QRCodeScanDisplay",
    data() {
        return {
            loading: true,
            error: false,
            errorMessage: "",
            branchData: null,
            showImageModal: false,
            activeIndex: 0
        };
    },
    computed: {
        /**
         * Supports both the new array field and the legacy single-photo field.
         * Filters out any null/empty entries.
         */
        certificatePhotos() {
            if (!this.branchData) return [];
            const arr = this.branchData.branch_certificate_photos;
            if (Array.isArray(arr) && arr.length) {
                return arr.filter(Boolean);
            }
            // Fallback to legacy single photo
            const single = this.branchData.branch_certificate_photo;
            return single ? [single] : [];
        },
        activePhoto() {
            return this.certificatePhotos[this.activeIndex] || null;
        }
    },
    mounted() {
        AppOptions.appEmpty = true;
        this.fetchBranchData();
    },
    methods: {
        async fetchBranchData() {
            try {
                const token = this.$route.query.token;

                if (!token) {
                    this.error = true;
                    this.errorMessage = this.$t("branch.tokenRequired");
                    this.loading = false;
                    return;
                }

                const response = await axios.post(
                    `${process.env.VUE_APP_PROXY}/api/branches/decode-qr-token`,
                    { token }
                );

                if (response.data.success) {
                    const branchId = response.data.data.branch_id;

                    const branchResponse = await axios.get(
                        `${process.env.VUE_APP_PROXY}/api/branches/${branchId}/qr-display`
                    );

                    if (branchResponse.data.success) {
                        this.branchData = branchResponse.data.data;
                    } else {
                        this.error = true;
                        this.errorMessage = this.$t("branch.branchNotFound");
                    }
                } else {
                    this.error = true;
                    this.errorMessage = this.$t("branch.invalidToken");
                }
            } catch (error) {
                this.error = true;
                this.errorMessage =
                    error.response?.data?.message ||
                    this.$t("branch.errorLoadingData");
                console.error("Error fetching branch data:", error);
            } finally {
                this.loading = false;
            }
        },
        openModal(index) {
            this.activeIndex = index;
            this.showImageModal = true;
        },
        prevPhoto() {
            this.activeIndex =
                (this.activeIndex - 1 + this.certificatePhotos.length) %
                this.certificatePhotos.length;
        },
        nextPhoto() {
            this.activeIndex =
                (this.activeIndex + 1) % this.certificatePhotos.length;
        }
    }
};
</script>

<style scoped lang="scss">
.qr-scan-display {
    min-height: 100vh;
    padding: 20px;

    // Loading State
    .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80vh;

        .spinner-wrapper {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            padding: 30px;
            border-radius: 20px;
            margin-bottom: 20px;
        }

        .loading-text {
            color: white;
            font-size: 18px;
            font-weight: 500;
        }
    }

    // Error State
    .error-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 80vh;
        text-align: center;

        .error-icon-wrapper {
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            padding: 30px;
            border-radius: 50%;
            margin-bottom: 20px;
            color: #ff4757;
        }

        .error-title {
            color: white;
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
        }

        .error-message {
            color: rgba(255, 255, 255, 0.9);
            font-size: 16px;
            margin-bottom: 30px;
            max-width: 400px;
        }
    }

    // Main Content
    .content-container {
        max-width: 900px;
        margin: 0 auto;
        background: white;
        border-radius: 24px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        overflow: hidden;
        animation: slideUp 0.5s ease-out;
    }

    @keyframes slideUp {
        from {
            opacity: 0;
            transform: translateY(30px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    // Company Header
    .company-header {
        padding: 40px 30px;
        border-bottom: 1px solid #e9ecef;

        .company-content {
            display: flex;
            align-items: center;
            gap: 24px;
            max-width: 800px;
            margin: 0 auto;
        }

        .logo-wrapper {
            flex-shrink: 0;

            .company-logo {
                width: 150px;
                object-fit: cover;
                transition: transform 0.3s ease;
                &:hover {
                    transform: scale(1.05);
                }
            }

            .no-logo {
                width: 100px;
                height: 100px;
                background: #f8f9fa;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #667eea;
                border: 4px solid #e9ecef;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
        }

        .company-info {
            flex: 1;
            text-align: left;

            .company-name-en {
                color: #3b86d1;
                font-size: 28px;
                font-weight: 700;
                margin: 0 0 8px 0;
                letter-spacing: -0.5px;
            }

            .company-name-kh {
                color: #3b86d1;
                font-size: 30px;
                margin: 0;
                font-weight: 500;
            }
        }
    }

    // Certificate Section
    .certificate-section {
        padding: 40px 30px;

        .section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 24px;
            color: #667eea;

            h3 {
                font-size: 22px;
                font-weight: 600;
                margin: 0;
            }
        }

        // Grid: up to 3 columns, collapses gracefully
        .certificate-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 16px;
        }

        .certificate-wrapper {
            position: relative;
            border-radius: 12px;
            overflow: hidden;
            cursor: pointer;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            aspect-ratio: 3/4;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);

                .image-overlay {
                    opacity: 1;
                }
            }

            .certificate-image {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
            }

            .image-overlay {
                position: absolute;
                inset: 0;
                background: rgba(102, 126, 234, 0.85);
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 10px;
                color: white;
                opacity: 0;
                transition: opacity 0.3s ease;

                span {
                    font-size: 14px;
                    font-weight: 500;
                }
            }
        }

        .no-certificate {
            padding: 80px 20px;
            background: #f8f9fa;
            border-radius: 12px;
            text-align: center;
            color: #adb5bd;
            border: 2px dashed #dee2e6;

            p {
                margin-top: 20px;
                font-size: 16px;
                font-weight: 500;
            }
        }
    }

    // Modal
    .modal-content {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .modal-image {
            width: 100%;
            height: auto;
            border-radius: 8px;
        }

        .modal-nav {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(0, 0, 0, 0.45);
            border: none;
            border-radius: 50%;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            cursor: pointer;
            transition: background 0.2s;
            z-index: 10;

            &:hover {
                background: rgba(0, 0, 0, 0.7);
            }

            &--prev {
                left: 8px;
            }
            &--next {
                right: 8px;
            }
        }

        .modal-counter {
            position: absolute;
            bottom: 12px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.5);
            color: white;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 13px;
        }
    }

    // Responsive
    @media (max-width: 768px) {
        padding: 10px;

        .company-header {
            padding: 30px 20px;

            .company-content {
                flex-direction: column;
                text-align: center;
                gap: 16px;
            }

            .logo-wrapper .company-logo,
            .logo-wrapper .no-logo {
                width: 80px;
                height: 80px;
            }

            .company-info {
                text-align: center;

                .company-name-en {
                    font-size: 22px;
                }
                .company-name-kh {
                    font-size: 26px;
                }
            }
        }

        .certificate-section {
            padding: 30px 20px;

            .certificate-grid {
                grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
                gap: 10px;
            }
        }
    }
}
</style>
